// Вкладка «Валюты»: admin CRUD GET/POST/PUT/DELETE /api/admin/currencies (V16 W11).
// Код при PUT не меняется. Delete и PUT active=false при in-use → 409, строка жива.

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Box,
  Button,
  Chip,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { LoadingSpinner } from '@/theme/LoadingSpinner';
import { EmptyState } from '@/theme/EmptyState';
import { adminDictionariesApi, type AdminCurrency } from '@/api/adminDictionaries';
import { extractApiErrorMessage } from '@/lib/apiError';
import { t } from '@/i18n';
import { CurrencyCreateDialog } from './components/CurrencyCreateDialog';
import { CurrencyEditDialog } from './components/CurrencyEditDialog';

/** staleTime admin-таблицы валют (инвалидация по мутациям). */
const CURRENCIES_ADMIN_STALE_TIME_MS = 5 * 60 * 1000;

/** Вкладка справочника валют: таблица, создание, правка, удаление и деактивация. */
export function CurrenciesTab(): JSX.Element {
  const queryClient = useQueryClient();
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<AdminCurrency | null>(null);
  const [pageError, setPageError] = useState<string | null>(null);

  const query = useQuery({
    queryKey: ['admin', 'currencies'],
    queryFn: () => adminDictionariesApi.listCurrencies(),
    staleTime: CURRENCIES_ADMIN_STALE_TIME_MS,
  });

  const invalidateCurrencies = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['admin', 'currencies'] });
    void queryClient.invalidateQueries({ queryKey: ['currencies'] });
  };

  const createMutation = useMutation({
    mutationFn: (payload: { code: string; symbol: string }) =>
      adminDictionariesApi.createCurrency(payload),
    onSuccess: () => {
      setPageError(null);
      setCreateOpen(false);
      invalidateCurrencies();
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({
      code,
      symbol,
      active,
    }: {
      code: string;
      symbol: string;
      active: boolean;
    }) => adminDictionariesApi.updateCurrency(code, { symbol, active }),
    onSuccess: () => {
      setPageError(null);
      setEditing(null);
      invalidateCurrencies();
    },
    onError: (error) => {
      setPageError(extractApiErrorMessage(error, t('dict.currencies.saveError')));
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (code: string) => adminDictionariesApi.deleteCurrency(code),
    onSuccess: () => {
      setPageError(null);
      invalidateCurrencies();
    },
    onError: (error) => {
      setPageError(extractApiErrorMessage(error, t('dict.currencies.deleteError')));
    },
  });

  const openCreate = (): void => {
    createMutation.reset();
    setCreateOpen(true);
  };

  const openEdit = (currency: AdminCurrency): void => {
    setEditing(currency);
    updateMutation.reset();
  };

  const submitEdit = (values: { symbol: string; active: boolean }): void => {
    if (!editing) return;
    updateMutation.mutate({ code: editing.code, ...values });
  };

  const deactivate = (currency: AdminCurrency): void => {
    updateMutation.mutate({ code: currency.code, symbol: currency.symbol, active: false });
  };

  const activate = (currency: AdminCurrency): void => {
    updateMutation.mutate({ code: currency.code, symbol: currency.symbol, active: true });
  };

  return (
    <Box>
      <Stack direction="row" justifyContent="flex-end" sx={{ mb: 2 }}>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
          {t('dict.currencies.create')}
        </Button>
      </Stack>
      {query.isLoading ? (
        <LoadingSpinner label={t('common.loading')} />
      ) : query.isError ? (
        <EmptyState
          title={t('dict.currencies.loadError')}
          description={
            query.error instanceof Error ? query.error.message : t('dict.currencies.loadErrorDesc')
          }
        />
      ) : !query.data || query.data.length === 0 ? (
        <EmptyState title={t('dict.currencies.empty')} description={t('dict.currencies.emptyDesc')} />
      ) : (
        <Paper>
          {pageError ? (
            <Alert severity="error" sx={{ m: 2 }}>
              {pageError}
            </Alert>
          ) : null}
          <TableContainer>
            <Table size="medium" aria-label={t('dict.tabs.currencies')}>
              <TableHead>
                <TableRow>
                  <TableCell>{t('dict.currencies.code')}</TableCell>
                  <TableCell>{t('dict.currencies.symbol')}</TableCell>
                  <TableCell>{t('dict.columns.status')}</TableCell>
                  <TableCell>{t('dict.columns.actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {query.data.map((currency) => (
                  <TableRow key={currency.code} hover>
                    <TableCell>{currency.code}</TableCell>
                    <TableCell>{currency.symbol}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={t(currency.active ? 'dict.status.active' : 'dict.status.inactive')}
                        color={currency.active ? 'success' : 'default'}
                      />
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <Tooltip title={t('common.edit')}>
                          <IconButton
                            size="small"
                            aria-label={t('common.edit')}
                            onClick={() => openEdit(currency)}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={t('common.delete')}>
                          <IconButton
                            size="small"
                            aria-label={t('common.delete')}
                            disabled={deleteMutation.isPending}
                            onClick={() => deleteMutation.mutate(currency.code)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {currency.active ? (
                          <Button
                            variant="outlined"
                            color="error"
                            size="small"
                            onClick={() => deactivate(currency)}
                            disabled={updateMutation.isPending}
                          >
                            {t('dict.deactivate')}
                          </Button>
                        ) : (
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => activate(currency)}
                            disabled={updateMutation.isPending}
                          >
                            {t('dict.activate')}
                          </Button>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      <CurrencyCreateDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onSubmit={(values) => createMutation.mutate(values)}
        isPending={createMutation.isPending}
        error={createMutation.isError ? createMutation.error : undefined}
      />
      <CurrencyEditDialog
        open={Boolean(editing)}
        currency={editing}
        onClose={() => setEditing(null)}
        onSubmit={submitEdit}
        isPending={updateMutation.isPending}
      />
    </Box>
  );
}
